import React, { memo, useEffect, useReducer } from 'react';

import { Paper } from '@material-ui/core';
import { combineReducers } from '@reduxjs/toolkit';
import { useSelectedView } from 'reducers/viewsSlice';
import styled from 'styled-components';

import {
  GOAL_PROGRESSES,
  SEARCH_TAG_TYPES,
  TASK_TYPES_2,
  TASK_TYPE_FILTER_OPTIONS,
  TEAM_TASKS_VIEW_TYPES,
} from 'constants/ProjectConstants';

import { universalSearchAutoCompleteV2 } from 'services/search';
import { getCohortTeamEmployees } from 'services/team';

import TaskSortDropdown from '../TeamTasksListView/TaskSortDropdown';
import { updateOrderByAction } from '../TeamTasksListView/orderByReducer';
import { completedOnFilterReducer, initialCompletedOnFilterState } from './CompletedOnFilter';
import EmployeeFilter, { employeeFilterReducer } from './EmployeeFilter';
import LastActivityDateFilter, {
  lastActivityDateFilterReducer,
  initialLastActivityDateFilterState,
} from './LastActivityDateFilter';
// import DueDateFilter, { dueDateFilterReducer, initialDueDateFilterState } from './DueDateFilter';
import { dueDateFilterReducer, initialDueDateFilterState } from './NewDueDateFilter';
import ObjectiveFilter, { initialObjectiveFilterState, objectiveFilterReducer } from './ObjectiveFilter';
import PrimaryPodFilter, { intialPrimaryPodFilters, primaryPodFilterReducer } from './PrimaryPodFilter';
import TagFilter, { tagFilterReducer, initialTagFilters } from './TagFilter';
import TaskTypeFilter, { intialTaskTypeFilters, taskTypeFilterReducer } from './TaskTypeFilter';
import { getPrimaryPodsList } from './helper';
import {
  employeesReducer,
  goalProgressReducer,
  primaryPodsReducer,
  taskTagsReducer,
  taskTypesReducer,
} from './reducers';

export const CLEAR_ALL_FILTERS = 'CLEAR_ALL_FILTERS';
export const RESET_ALL_FILTERS = 'RESET_ALL_FILTERS';
const FILTERS_UPDATE = 'FILTERS_UPDATE';

const FILTER_DATA_UPDATE = 'FILTER_DATA_UPDATE';

export const initialFiltersState = {
  primaryPodFilters: intialPrimaryPodFilters,
  taskTypeFilters: { ...intialTaskTypeFilters },
  employeeFilters: {},
  objectiveFilters: initialObjectiveFilterState,
  dueDateFilters: { ...initialDueDateFilterState },
  completedOnFilters: { ...initialCompletedOnFilterState },
  tagFilters: { ...initialTagFilters },
  lastActivityDateFilters: { ...initialLastActivityDateFilterState },
};

const initialDataState = {
  primaryPods: [],
  taskTypes: [],
  employees: [],
  goalProgress: GOAL_PROGRESSES,
};

export const newFiltersReducer = combineReducers({
  dueDateFilters: dueDateFilterReducer,
  primaryPodFilters: primaryPodFilterReducer,
  taskTypeFilters: taskTypeFilterReducer,
  employeeFilters: employeeFilterReducer,
  objectiveFilters: objectiveFilterReducer,
  completedOnFilters: completedOnFilterReducer,
  lastActivityDateFilters: lastActivityDateFilterReducer,
  tagFilters: tagFilterReducer,
});

const combinedDataReducer = combineReducers({
  goalProgress: goalProgressReducer,
  primaryPods: primaryPodsReducer,
  taskTypes: taskTypesReducer,
  employees: employeesReducer,
  taskTags: taskTagsReducer,
});

function dataReducer(state, action) {
  switch (action.type) {
    case FILTER_DATA_UPDATE:
      return { ...state, ...action.data };
    default:
      return combinedDataReducer(state, action);
  }
}

export function filtersReducersWithClearAll(state, action) {
  switch (action.type) {
    case CLEAR_ALL_FILTERS:
      return { ...state, ...initialFiltersState };
    case RESET_ALL_FILTERS:
      return { ...initialFiltersState, ...action.data };
    default:
      return newFiltersReducer(state, action);
  }
}

// selectedTaskType = "GOALS" or "TASKS"
function TeamPageFilters({
  selectedTaskType,
  teamId,
  newFiltersDispatch: dispatch,
  newFiltersState,
  orderBy,
  order,
  orderByDispatch,
  teamTasksViewType,
  selectedTimeBoundPeriod = null,
}) {
  const [{ employees, primaryPods, taskTypes, taskTags }, dataDispatch] = useReducer(dataReducer, initialDataState);
  const selectedCohort = useSelectedView();

  const {
    primaryPodFilters,
    taskTypeFilters,
    employeeFilters,
    dueDateFilters,
    objectiveFilters,
    tagFilters,
    completedOnFilters = {},
    lastActivityDateFilters,
  } = newFiltersState;

  useEffect(() => {
    // const teamsPrimaryPodDataPromise = getTeams({ teamId })
    //   .then(convertEmployeeRes)
    //   .then(employees => {
    //     employeesData = employees;
    //     const empIds = employees.map(emp => emp.value);
    //     if (employees.length > 0) return getPrimaryPodsList(empIds);
    //     else return [];
    //   })
    //   .then(primaryPods => ({ employees: employeesData, primaryPods }));
    let employeesData = [];
    const cohortEmployeesPromise = getCohortTeamEmployees({
      selectedCohort,
    })
      .then((employees) => {
        employeesData = employees;
        const empIds = employees.map((emp) => emp.value);
        if (employees.length > 0) return getPrimaryPodsList(empIds);
        return Promise.resolve([]);
      })
      .then((primaryPods) => ({ employees: employeesData, primaryPods }));

    const tagsSet = [
      {
        tagType: 'MESH_TASK_TAG',
        limit: 100,
      },
      {
        tagType: 'GOAL_TAG',
        limit: 100,
      },
    ];

    const tagsDataPromise = universalSearchAutoCompleteV2({
      text: '',
      tagsSet,
    }).then((res) => {
      const taskTagsResult = res ? res.filter((r) => r.label === SEARCH_TAG_TYPES.taskTag) : [];
      // const goalTagsResult = res ? res.filter(r => r.label === SEARCH_TAG_TYPES.goalTag) : [];
      const taskTags = taskTagsResult.length
        ? taskTagsResult[0].options.map((tag) => ({ label: tag.label, value: tag.id }))
        : [];
      // const goalTags =  goalTagsResult.length
      // ? goalTagsResult[0].options.map(tag => ({ label: tag.label, value: tag.id }))
      // : []
      return taskTags;
      // dataDispatch({ type: TASK_TAGS_UPDATE, data: { taskTags: taskTags } });
    });

    Promise.all([
      // teamsPrimaryPodDataPromise,
      tagsDataPromise,
      cohortEmployeesPromise,
    ])
      .then(
        ([
          // teamsData,
          tagsData,
          employeesData,
        ]) => {
          dataDispatch({
            type: FILTER_DATA_UPDATE,
            data: {
              taskTags: tagsData,

              employees: employeesData.employees,
              primaryPods: employeesData.primaryPods,
              taskTypes: TASK_TYPE_FILTER_OPTIONS,
            },
          });
        }
      )
      .catch((err) => {
        console.error('An error occurred while fetching data for filters');
        console.error(err);
      });
  }, [teamId, selectedCohort]);

  function clearAll() {
    dispatch({ type: CLEAR_ALL_FILTERS });
  }

  // sorting

  const handleRequestSort = (event, property, type = null) => {
    const isAsc = orderBy === property && order === 'asc';
    updateOrderByAction({ type: type || (isAsc ? 'desc' : 'asc'), field: property })(orderByDispatch);
  };
  const createSortHandler =
    (property, type = null) =>
    (event) => {
      event.stopPropagation();
      handleRequestSort(event, property, type);
    };

  return (
    <Container elevation={0}>
      {/* <DummyFilterDisplay /> */}
      {selectedTaskType === TASK_TYPES_2.task ? (
        <TaskTypeFilter
          selectedFilters={taskTypeFilters}
          dispatch={dispatch}
          taskTypes={taskTypes}
          selectedTaskType={selectedTaskType}
          width="20%"
        />
      ) : (
        <ObjectiveFilter
          selectedFiltersDispatch={dispatch}
          selectedFilters={objectiveFilters}
          width={selectedTaskType === TASK_TYPES_2.task ? '20%' : '25%'}
          isDisabled={TEAM_TASKS_VIEW_TYPES.goalTree === teamTasksViewType}
          selectedTimeBoundPeriod={selectedTimeBoundPeriod}
        />
      )}
      <EmployeeFilter
        dispatch={dispatch}
        teamEmployees={employees}
        selectedFilters={employeeFilters}
        width={selectedTaskType === TASK_TYPES_2.task ? '20%' : '25%'}
        selectedTaskType={selectedTaskType}
      />
      <LastActivityDateFilter
        selectedFiltersDispatch={dispatch}
        selectedFilters={lastActivityDateFilters}
        width={selectedTaskType === TASK_TYPES_2.task ? '20%' : '25%'}
        selectedTaskType={selectedTaskType}
      />
      {selectedTaskType === TASK_TYPES_2.task ? (
        <TagFilter selectedFiltersDispatch={dispatch} selectedFilters={tagFilters} width="20%" taskTags={taskTags} />
      ) : (
        <PrimaryPodFilter
          selectedFilters={primaryPodFilters}
          dispatch={dispatch}
          primaryPods={primaryPods}
          width={selectedTaskType === TASK_TYPES_2.task ? '20%' : '25%'}
        />
      )}
      {/* <ProgressFilter data={goalProgress} dispatch={dispatch} selectedFilters={progressFilters} /> */}

      {/* <CompletedOnFilter
        selectedFiltersDispatch={dispatch}
        selectedFilters={completedOnFilters}
        width="18%"
      /> */}

      {selectedTaskType === TASK_TYPES_2.task && (
        <TaskSortDropdown
          createSortHandler={createSortHandler}
          orderBy={orderBy}
          order={order}
          orderByDispatch={orderByDispatch}
          width="20%"
          isDisabled={teamTasksViewType !== TEAM_TASKS_VIEW_TYPES.list}
          selectedTaskType={selectedTaskType}
        />
      )}

      {/* <DueDateFilter
        selectedFiltersDispatch={dispatch}
        selectedFilters={dueDateFilters}
        width="18%"
      /> */}
      {/* <ItemContainer width="10%" onClick={clearAll} justify="center">
        <StyledTypography leftGutter={1} display="inline">
          Clear
        </StyledTypography>
      </ItemContainer> */}
    </Container>
  );
}

export default memo(TeamPageFilters);

const Container = styled(Paper)`
  display: flex;
  margin: 24px 0;
  border: 1px solid #eaeaea;
  position: relative;
  z-index: 2;
`;
